import { useQuery } from "@tanstack/react-query";
import { Button } from "antd";
import { useMemo, useState } from "react";
import ReportingExportButton from "../../../components/Button/ReportingExportButton";
import AuditLogModal from "../../../components/Modal/AuditLogModal";
import MocPDFTemplate from "../../../components/PDF/Reporting/moc/MocPDFTemplate";
import QueryRetry from "../../../components/QueryRetry";
import ReportItemsPanel from "../../../components/Reporting/items/ReportItemsPanel";
import ReportCreatedLine from "../../../components/Reporting/ReportCreatedLine";
import {
	getAssetCard,
	getProjectFacts,
	getReviewerFact,
	getTenantFact,
	LookedUpValue,
} from "../../../components/Reporting/reportFacts";
import ReportHeader, {
	ReportHeaderFact,
} from "../../../components/Reporting/ReportHeader";
import { useGlobalLoading } from "../../../hooks/useGlobalLoading";
import { useLookup } from "../../../hooks/useLookup";
import useReportDocumentsDrawer from "../../../hooks/useReportDocumentsDrawer";
import useReportEditor from "../../../hooks/useReportEditor";
import useReportItemNames from "../../../hooks/useReportItemNames";
import { getMoCList } from "../../../services/api-server/moc";
import { GetAntIcon } from "../../../utils/ant_icons";
import { createMocLookupConfig } from "../../../utils/reporting/lookupConfigs/moc";
import { getReportAuthorEmail } from "../../../utils/reporting/reportAuthor";
import { REPORT_ITEM_KINDS } from "../../../utils/reporting/reportItemKinds";
import { getReportStatusDisplay } from "../../../utils/reporting/statusDisplay";
import { handleExportReport } from "../../../utils/reports";

interface MocOverviewProps {
	reportId: string;
}

const ACTIONS = REPORT_ITEM_KINDS.follow_up_actions;

// The form's "risk assessment completed" switch. A MoC saved before the
// switch existed has no answer.
const getRiskAssessmentLabel = (completed?: boolean) => {
	if (completed === true) return "Completed";
	if (completed === false) return "Not completed";
	return "—";
};

function MocOverview({ reportId }: Readonly<MocOverviewProps>) {
	const [auditLogOpen, setAuditLogOpen] = useState(false);

	const { data, isLoading, isError } = useQuery({
		queryKey: ["report-overview", reportId],
		queryFn: async () => {
			const response: any = await getMoCList({ _id: reportId });
			return response[0] ?? null;
		},
		enabled: !!reportId,
	});

	const editor = useReportEditor("moc", data, ["report-overview", reportId]);
	const documents = useReportDocumentsDrawer("moc", reportId, data);

	useGlobalLoading("moc-overview", isLoading);

	const isRejected = data?.is_rejected === true;
	const statusDisplay = getReportStatusDisplay(
		data?.status,
		data?.open_status,
		isRejected
	);

	const lookupConfig = useMemo(
		() => createMocLookupConfig([data?.operator]),
		[data?.operator]
	);

	const { dataWithLookup, isLoading: isLoadingLookup } = useLookup(
		["lookup", "report-overview", reportId],
		data ? [data] : [],
		lookupConfig,
		0
	);
	const lookupData = dataWithLookup[0];

	// From the looked-up report, so the export keeps the department and
	// category names the PDF prints.
	const { emailToName, reportWithNames, isLoadingNames } = useReportItemNames(
		["report-actions-responsible", reportId],
		lookupData,
		ACTIONS
	);

	if (isError) {
		// The drawers stay, so a failed background refetch doesn't close them.
		return (
			<>
				<QueryRetry queryKey={["report-overview", reportId]} />
				{documents.drawer}
				{editor.drawer}
			</>
		);
	}

	const facts: ReportHeaderFact[] = [
		{
			key: "department",
			label: "Department",
			value: (
				<LookedUpValue
					value={lookupData?.department_name}
					isLoading={isLoadingLookup}
				/>
			),
		},
		{
			key: "category",
			label: "MoC Category",
			value: (
				<LookedUpValue
					value={lookupData?.category_name}
					isLoading={isLoadingLookup}
				/>
			),
		},
		...getProjectFacts(lookupData, isLoadingLookup),
		getReviewerFact(lookupData, isLoadingLookup),
		{
			key: "risk_assessment",
			label: "Risk assessment",
			value: getRiskAssessmentLabel(data?.risk_assessment_completed),
		},
		getTenantFact(data?.operator),
	];

	const created = (
		<ReportCreatedLine
			submittedOn={data?.submitted_on}
			authorName={lookupData?.author_name}
			authorEmail={getReportAuthorEmail(data)}
			historyTitle="Management of Change review history"
			historyData={lookupData ?? data}
			onOpenAuditLog={() => setAuditLogOpen(true)}
		/>
	);

	return (
		<>
			{data && (
				// Keyed by report, like the actions panel: another MoC opened in
				// place starts expanded, with its description clamped.
				<ReportHeader
					key={reportId}
					label="MoC details"
					reportNumber={data.report_number}
					title={data.title}
					status={statusDisplay}
					actions={
						<>
							<Button icon={GetAntIcon("file2")} onClick={documents.open}>
								Additional documents
							</Button>
							<ReportingExportButton
								type="default"
								onExport={() =>
									handleExportReport(reportWithNames, "moc", MocPDFTemplate)
								}
								isLoading={isLoadingLookup || isLoadingNames}
							/>
						</>
					}
					created={created}
					facts={facts}
					media={getAssetCard(lookupData, isLoadingLookup)}
					summaries={[
						{
							key: "description",
							label: "Brief description",
							text: data.description,
						},
					]}
				>
					{/* Keyed by report, so another MoC opened in place starts with
					    its own filters and open rows. */}
					<ReportItemsPanel
						key={reportId}
						kind={ACTIONS}
						items={reportWithNames?.follow_up_actions ?? []}
						emailToName={emailToName}
						report={data}
						canEditItem={editor.canEditItem}
						onEditItem={editor.openItem}
						onAddItems={editor.canEdit ? editor.open : undefined}
					/>
				</ReportHeader>
			)}
			{/* Outside the header, so collapsing it never closes the log. */}
			{data && (
				<AuditLogModal
					auditlogFilter={{ level: "moc", level_id: data._id }}
					label="Management of Change"
					type="reporting"
					open={auditLogOpen}
					handleClose={() => {
						setAuditLogOpen(false);
					}}
				/>
			)}
			{documents.drawer}
			{editor.drawer}
		</>
	);
}

export default MocOverview;
